// Campaign-only artist-message selection. Labels were audited against what each
// original screenshot actually shows; several shared filenames/alt texts do not
// match their content (e.g. `killboy.jpg` is a Vince Millett post), so labels
// here describe the visible image, never the filename. Where no name is visible,
// the label stays neutral. Width/height are the real file dimensions so the
// layout is reserved before the image loads.
import vinceSpotify from "@/assets/testimonials/killboy.jpg";
import andrew500k from "@/assets/testimonials/andrew-gemmell.jpg";
import darnellEditorials from "@/assets/testimonials/artist-traction-building.jpg";
import dmEditorials from "@/assets/testimonials/vince-millett.jpeg";
import jesseFirstSingle from "@/assets/testimonials/taylor-grey-listener-growth.jpg";
import mauro11k from "@/assets/testimonials/sammy-arriaga-streaming-growth.jpg";
import michaelSync from "@/assets/testimonials/academy-results-course.jpg";
import diego130k from "@/assets/testimonials/artist-struggling-hope.jpg";
import robbGrant from "@/assets/testimonials/kaylee-bell-tiktok-views.jpeg";
import ronQuerian from "@/assets/testimonials/sean-stephens-tiktok-growth.jpeg";
import aprilJai from "@/assets/testimonials/label-artist-momentum.jpg";
import gerryEditorial from "@/assets/testimonials/academy-practical-valuable.jpg";
import dm1m from "@/assets/testimonials/andrew-gemmell-sam-sounds-2-8m.jpg";
import francesco from "@/assets/testimonials/tiffany-woys-spotify-growth.jpg";
import timBbc from "@/assets/testimonials/spotify-curator-repost.jpg";
import williamManzano from "@/assets/testimonials/william-manzano.jpg";
import rosan from "@/assets/testimonials/artist-breakthrough-moment.jpg";
import joyHansi from "@/assets/testimonials/kelly-paige-momentum.jpg";

import carloAllNewRock from "@/assets/testimonials/22k-apple-music-nmd.jpg";
import stergiosReleasePlan from "@/assets/testimonials/andrew-gemmell-60k-followers.jpg";
import shannonBbc from "@/assets/testimonials/artist-editorial-wins.jpg";
import noahThirdSingle from "@/assets/testimonials/curator-love.jpeg";
import cordeliaSupport from "@/assets/testimonials/david-kuperman-game-changer.jpg";
import davidEngagement from "@/assets/testimonials/david-kuperman.jpg";
import cheriseReelReach from "@/assets/testimonials/drcherise-joy-45k-viral.jpg";
import rexEncouragement from "@/assets/testimonials/emma-stevens-200k-spotify.jpg";
import philFolkChart from "@/assets/testimonials/gerry-matthee.jpg";
import alexFgMilestones from "@/assets/testimonials/hansi-tiktok-500k-views.jpg";
import joyFrostWrapped from "@/assets/testimonials/joy-frost-stats.jpg";
import paulWeFoundLove from "@/assets/testimonials/katie-cole-multiple-playlists.jpg";
import lukinDirection from "@/assets/testimonials/lukin-joshua-festival-booking.jpg";
import michaelDebut from "@/assets/testimonials/michael-gilbride-20k-streams.jpeg";
import chrisYearProgress from "@/assets/testimonials/mike-dimes-10m-streams.jpg";
import elissaRadioOne from "@/assets/testimonials/spencer-crandall-1m-streams.jpg";
import thomasCommunity from "@/assets/testimonials/chris-pierce-growth.jpeg";
import dmClearerDirection from "@/assets/testimonials/spotify-curator-real-connection.jpg";

export interface ArtistMessage {
  id: string;
  src: string;
  width: number;
  height: number;
  /** Who/what the screenshot visibly shows. */
  label: string;
  alt: string;
}

export const ARTIST_MESSAGES: ArtistMessage[] = [
  {
    id: "vince-millett-wrapped",
    src: vinceSpotify,
    width: 1000,
    height: 1186,
    label: "Vince Millett",
    alt: "Vince Millett community post sharing his Spotify Wrapped, with a reply from Damian",
  },
  {
    id: "andrew-gemmell-500k",
    src: andrew500k,
    width: 1000,
    height: 586,
    label: "Andrew Gemmell",
    alt: "Andrew Gemmell post about a song passing 500K streams",
  },
  {
    id: "darnell-butler-editorials",
    src: darnellEditorials,
    width: 1000,
    height: 668,
    label: "Darnell Butler",
    alt: "Darnell Butler post about 9 Apple Music editorials, 2 distro deals and a publishing deal",
  },
  {
    id: "dm-two-editorials",
    src: dmEditorials,
    width: 1000,
    height: 597,
    label: "Direct message from an artist",
    alt: "Message to Damian about landing two Spotify editorial playlists",
  },
  {
    id: "jesse-shafer-first-single",
    src: jesseFirstSingle,
    width: 1000,
    height: 507,
    label: "Jesse Shafer",
    alt: "Jesse Shafer post about his release day after five years on Spotify",
  },
  {
    id: "mauro-lacandia-11k",
    src: mauro11k,
    width: 1000,
    height: 781,
    label: "Mauro Lacandia",
    alt: "Mauro Lacandia post about passing 11,000 streams in under a month",
  },
  {
    id: "michael-gilbride-sync",
    src: michaelSync,
    width: 1000,
    height: 690,
    label: "Michael Gilbride",
    alt: "Michael Gilbride post about a sync deal and a debut single passing 100K",
  },
  {
    id: "diego-solana-130k",
    src: diego130k,
    width: 1000,
    height: 559,
    label: "Diego Solana Trueba",
    alt: "Diego Solana Trueba post about 130K views in two weeks",
  },
  {
    id: "robb-grant",
    src: robbGrant,
    width: 1000,
    height: 480,
    label: "Robb Grant",
    alt: "Robb Grant comment about having Damian in your corner",
  },
  {
    id: "ron-querian",
    src: ronQuerian,
    width: 1000,
    height: 434,
    label: "Ron Querian",
    alt: "Ron Querian comment about his streams increasing six times",
  },
  {
    id: "april-jai-nmf",
    src: aprilJai,
    width: 1000,
    height: 703,
    label: "April Jai",
    alt: "April Jai post about a single added to New Music Friday UK",
  },
  {
    id: "gerry-matthee-editorial",
    src: gerryEditorial,
    width: 1000,
    height: 996,
    label: "Gerry Matthee",
    alt: "Gerry Matthee post about landing editorial playlists on release day",
  },
  {
    id: "dm-1m-streams",
    src: dm1m,
    width: 1000,
    height: 640,
    label: "Direct message from an artist",
    alt: "Message to Damian about reaching 1 million streams on Spotify",
  },
  {
    id: "francesco-pirrone",
    src: francesco,
    width: 1000,
    height: 233,
    label: "Francesco Pirrone",
    alt: "Francesco Pirrone comment about growing to over 22K TikTok followers",
  },
  {
    id: "tim-white-bbc",
    src: timBbc,
    width: 1000,
    height: 948,
    label: "Tim White",
    alt: "Tim White post about a debut single getting BBC Introducing play",
  },
  {
    id: "william-manzano",
    src: williamManzano,
    width: 1000,
    height: 373,
    label: "William Elvin Manzano",
    alt: "William Elvin Manzano comment about subscribing to Damian Keyes",
  },
  {
    id: "rosan-duffour",
    src: rosan,
    width: 1000,
    height: 441,
    label: "Rosan Sashida Duffour",
    alt: "Rosan Sashida Duffour post about 4,700 organic Spotify plays on release day",
  },
  {
    id: "joy-hansi",
    src: joyHansi,
    width: 1000,
    height: 652,
    label: "Joy Hansi",
    alt: "Joy Hansi post about her first paid reach ad on TikTok",
  },
  {
    id: "carlo-passavanti-rock-editorial",
    src: carloAllNewRock,
    width: 1000,
    height: 1219,
    label: "Carlo Passavanti",
    alt: "Carlo Passavanti post about his band’s single joining All New Rock, with a reply from Damian",
  },
  {
    id: "stergios-kourou-release-plan",
    src: stergiosReleasePlan,
    width: 1000,
    height: 1229,
    label: "Stergios Kourou",
    alt: "Stergios Kourou post thanking Damian for the release plan used for his band’s singles and album",
  },
  {
    id: "shannon-bbc-introducing",
    src: shannonBbc,
    width: 1000,
    height: 1426,
    label: "Shannon",
    alt: "Shannon post about Ella’s Song being selected for BBC Music Introducing in Essex",
  },
  {
    id: "noah-sonnen-third-single",
    src: noahThirdSingle,
    width: 1000,
    height: 1160,
    label: "Noah Sonnen",
    alt: "Noah Sonnen post about his third single receiving radio play and reaching 2,000 streams, with a reply from Damian",
  },
  {
    id: "cordelia-hagmann-support",
    src: cordeliaSupport,
    width: 1000,
    height: 733,
    label: "Cordelia Hagmann",
    alt: "Cordelia Hagmann post thanking Damian and the team for the courses, genuine support and her progress",
  },
  {
    id: "david-kuperman-engagement",
    src: davidEngagement,
    width: 1000,
    height: 908,
    label: "David Kuperman",
    alt: "David Kuperman post about listener engagement, more than 200 saves and his first song reaching 10,000 streams",
  },
  {
    id: "drcherise-joy-reel-reach",
    src: cheriseReelReach,
    width: 1000,
    height: 1331,
    label: "DrCherise Joy Thompson",
    alt: "DrCherise Joy Thompson post thanking Damian after a reel reached more than 45,000 accounts, with original insights",
  },
  {
    id: "rex-arcum-encouragement",
    src: rexEncouragement,
    width: 1000,
    height: 784,
    label: "Rex Arcum",
    alt: "Rex Arcum post thanking Damian for the courses and encouragement around his music career",
  },
  {
    id: "phil-cooper-folk-chart",
    src: philFolkChart,
    width: 1000,
    height: 590,
    label: "Phil Cooper",
    alt: "Phil Cooper post about spending over half the year in the official UK folk albums chart, with a reply from Damian",
  },
  {
    id: "alex-fg-youtube-spotify",
    src: alexFgMilestones,
    width: 1000,
    height: 573,
    label: "Alex FG",
    alt: "Alex FG comment crediting Damian’s strategies with helping him reach 1 million YouTube views and 200,000 Spotify streams",
  },
  {
    id: "joy-frost-wrapped",
    src: joyFrostWrapped,
    width: 1000,
    height: 1271,
    label: "Joy Frost",
    alt: "Joy Frost message celebrating a first million milestone alongside her Spotify Wrapped showing 1.6 million streams",
  },
  {
    id: "paul-middleton-we-found-love",
    src: paulWeFoundLove,
    width: 1000,
    height: 1466,
    label: "Paul Middleton",
    alt: "Paul Middleton post thanking Damian, Ella and the community as his cover of We Found Love passes 100,000 streams",
  },
  {
    id: "lukin-joshua-direction",
    src: lukinDirection,
    width: 1000,
    height: 1384,
    label: "Lukin Joshua Potgieter",
    alt: "Lukin Joshua Potgieter post about finding structure and a target audience, connecting with people and booking a festival slot",
  },
  {
    id: "michael-gilbride-debut",
    src: michaelDebut,
    width: 1000,
    height: 810,
    label: "Michael Gilbride",
    alt: "Michael Gilbride post celebrating Telco’s debut single and sharing its original streaming milestone",
  },
  {
    id: "chris-logan-year-progress",
    src: chrisYearProgress,
    width: 1000,
    height: 1261,
    label: "Chris Logan",
    alt: "Chris Logan post about first editorial playlists, festival slots and progress towards a sustainable music career",
  },
  {
    id: "elissa-franceschi-radio-one",
    src: elissaRadioOne,
    width: 1000,
    height: 444,
    label: "Elissa Franceschi",
    alt: "Elissa Franceschi post about her single Byron being selected for Radio 1’s Future Artists mixtape",
  },
  {
    id: "thomas-mills-community",
    src: thomasCommunity,
    width: 1000,
    height: 304,
    label: "Thomas Mills",
    alt: "Thomas Mills comment about the community Damian created and artists helping each other",
  },
  {
    id: "dm-clearer-direction",
    src: dmClearerDirection,
    width: 1000,
    height: 674,
    label: "Direct message from an artist",
    alt: "Message thanking Damian for a clearer direction through the roadmap course and saving time and money",
  },
];

export const INITIAL_MESSAGES = 3;
export const MESSAGES_STEP = 6;
